import 'src/global.css';

import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { CONFIG } from 'src/global-config';
import { themeConfig, ThemeProvider } from 'src/theme';

import {
  ChatHistoryContextProvider,
  SettingsProvider as ChatSettingsProvider,
} from 'src/sections/chat';

import { AuthProvider as MsalProvider } from 'src/auth/context/msal';

import { Snackbar } from './components/snackbar';
import { MotionLazy } from './components/animate/motion-lazy';
import { defaultSettings, SettingsProvider as ThemeSettingsProvider } from './components/settings';

const AuthProvider = (CONFIG.auth.method === 'msal' && MsalProvider) || MsalProvider;

type AppProps = {
  children: React.ReactNode;
};

const queryClient = new QueryClient({});

function App({ children }: AppProps) {
  useEffect(() => {
    const dynatraceUrl = import.meta.env.VITE_DYNATRACE_URL;
    if (dynatraceUrl) {
      // For local test only as it'll only inject once during build time
      const existingScript = document.querySelector(`script[src="${dynatraceUrl}"]`);
      if (existingScript) {
        console.log('Dynatrace script already exists, skipping injection');
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = dynatraceUrl;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeSettingsProvider defaultSettings={defaultSettings}>
          <ThemeProvider noSsr defaultMode="light" modeStorageKey={themeConfig.modeStorageKey}>
            <Snackbar />
            <ChatSettingsProvider>
              <ChatHistoryContextProvider>
                <MotionLazy>{children}</MotionLazy>
              </ChatHistoryContextProvider>
            </ChatSettingsProvider>
          </ThemeProvider>
        </ThemeSettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
